"use client";

import { useCallback, useMemo, useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboardOrThrow } from "@/lib/clipboard";
import {
  type BlockQuoteStyle,
  countParagraphs,
  countWords,
  mlaLineCount,
  shouldBeBlockQuote,
} from "@/lib/tool-kit/reference-checkers/block-quote";

/**
 * The block quote formatter's interactive half.
 *
 * Its page shell used to live in this component rather than in `page.tsx` — the client
 * file rendered the breadcrumb, the H1, the FAQ and the related links as well as the
 * tool. That is why this page's prose was unreachable to a translator even though its
 * `page.tsx` was a server component, and it is the shape five of the 250 tools are in.
 */

type Style = BlockQuoteStyle;

/** English strings. A translated content file overrides these through `ui`. */
export const BLOCK_QUOTE_FORMATTER_STRINGS = {
  styleLabel: "Citation Style",
  quoteLabel: "Quote Text",
  quotePlaceholder: "Paste your quote here...",
  citationLabel: "Citation (page/paragraph number)",
  previewHeading: "Formatted Preview",
  copy: "Copy",
  copied: "Copied!",
  isBlock: "This quote qualifies as a block quote.",
  isInline: "This quote is short enough for an inline quote.",
  words: "words",
  lines: "lines",
  paragraphs: "paragraph(s)",
  rulesHeading: "{style} Block Quote Rules",
  threshold: "Threshold:",
  indent: "Indent:",
  /** MLA only: verse is set off at four lines, prose only past four. */
  verseLabel: "Quoting poetry (verse lines)",
  /** The counter under the detection verdict, one wording per style. */
  apaCount: "{words} words (block quote at 40+)",
  mlaCount:
    "about {lines} lines at 6.5 in, an estimate (block quote above 4 lines of prose)",
  mlaVerseCount: "{lines} lines (block quote at 4+ lines of verse)",
  chicagoCount:
    "{words} words, {paragraphs} paragraph(s) (block quote at 100+ words or 2+ paragraphs)",
  styleNames: {
    apa: "APA (7th Edition)",
    mla: "MLA (9th Edition)",
    chicago: "Chicago (18th Edition)",
  } as Record<string, string>,
  thresholds: {
    apa: "40+ words",
    mla: "More than 4 lines of prose, or 4+ lines of poetry",
    chicago: "100+ words or 2+ paragraphs",
  } as Record<string, string>,
  indents: {
    apa: "0.5 inch (1.27 cm) from the left margin",
    mla: "0.5 inch (1.27 cm) from the left margin",
    chicago: "0.5 inch (1.27 cm) from the left margin",
  } as Record<string, string>,
  extras: {
    apa: "No quotation marks. Citation appears after the closing period.",
    mla: "No quotation marks. Citation appears after the closing period.",
    chicago: "No quotation marks. May reduce font size by one point.",
  } as Record<string, string>,
  /** Placeholder and fallback citation text, per style. */
  citationPlaceholders: {
    apa: "Smith, 2024, p. 15",
    mla: "Smith 15",
    chicago: "Smith 2024, 15",
  } as Record<string, string>,
  citationFallbacks: {
    apa: "Author, Year, p. X",
    mla: "Author X",
    chicago: "Author Year, X",
  } as Record<string, string>,
};

type Strings = typeof BLOCK_QUOTE_FORMATTER_STRINGS;

const STYLE_IDS: Style[] = ["apa", "mla", "chicago"];

export function buildPreview(
  quote: string,
  citation: string,
  style: Style,
  isBlock: boolean,
  strings: Strings
): string {
  if (!quote.trim()) {
    return "";
  }
  const citText = citation.trim() || strings.citationFallbacks[style];

  if (!isBlock) {
    return `"${quote.trim()}" (${citText}).`;
  }

  const indentedText = quote
    .trim()
    .split("\n")
    .map((line) => `    ${line}`)
    .join("\n");

  // APA 7 §8.27, MLA 9 and Chicago author-date: the citation follows the final punctuation
  // of the block, on the same line, with no period after it.
  return `${indentedText} (${citText})`;
}

function countLine(
  style: Style,
  verse: boolean,
  counts: { words: number; lines: number; paragraphs: number },
  strings: Strings
): string {
  const templates: Record<Style, string> = {
    apa: strings.apaCount,
    mla: verse ? strings.mlaVerseCount : strings.mlaCount,
    chicago: strings.chicagoCount,
  };
  return templates[style]
    .replace("{words}", String(counts.words))
    .replace("{lines}", String(counts.lines))
    .replace("{paragraphs}", String(counts.paragraphs));
}

export default function BlockQuoteFormatterWidget() {
  const strings = useToolUi(BLOCK_QUOTE_FORMATTER_STRINGS);
  const [quote, setQuote] = useState("");
  const [citation, setCitation] = useState("");
  const [style, setStyle] = useState<Style>("apa");
  const [verse, setVerse] = useState(false);
  const [copied, setCopied] = useState(false);

  const isBlock = useMemo(
    () => shouldBeBlockQuote(quote, style, verse),
    [quote, style, verse]
  );
  const wordCount = useMemo(() => countWords(quote), [quote]);
  const lineCount = useMemo(() => mlaLineCount(quote, verse), [quote, verse]);
  const paragraphCount = useMemo(() => countParagraphs(quote), [quote]);

  const formattedPreview = useMemo(
    () => buildPreview(quote, citation, style, isBlock, strings),
    [quote, citation, style, isBlock, strings]
  );

  const handleCopy = useCallback(async () => {
    if (!formattedPreview) {
      return;
    }
    try {
      await writeToClipboardOrThrow(formattedPreview);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API might not be available
    }
  }, [formattedPreview]);

  return (
    <>
      <section className="space-y-3">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="style-select"
        >
          {strings.styleLabel}
        </label>
        <select
          className="w-full rounded-lg border border-border bg-card px-4 py-2.5 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          id="style-select"
          onChange={(e) => setStyle(e.target.value as Style)}
          value={style}
        >
          {STYLE_IDS.map((id) => (
            <option key={id} value={id}>
              {strings.styleNames[id]}
            </option>
          ))}
        </select>
        {style === "mla" && (
          <label className="flex items-center gap-2 text-foreground text-sm">
            <input
              checked={verse}
              className="size-4 rounded border-border"
              onChange={(e) => setVerse(e.target.checked)}
              type="checkbox"
            />
            {strings.verseLabel}
          </label>
        )}
      </section>

      <section className="rounded-lg border border-border bg-muted/30 p-5">
        <h2 className="mb-3 font-semibold text-foreground text-sm">
          {strings.rulesHeading.replace("{style}", strings.styleNames[style])}
        </h2>
        <ul className="space-y-1.5 text-muted-foreground text-sm">
          <li>
            <strong>{strings.threshold}</strong> {strings.thresholds[style]}
          </li>
          <li>
            <strong>{strings.indent}</strong> {strings.indents[style]}
          </li>
          <li>{strings.extras[style]}</li>
        </ul>
      </section>

      <section className="space-y-3">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="quote-input"
        >
          {strings.quoteLabel}
        </label>
        <textarea
          className="min-h-[160px] w-full rounded-lg border border-border bg-card px-4 py-3 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="quote-input"
          onChange={(e) => setQuote(e.target.value)}
          placeholder={strings.quotePlaceholder}
          value={quote}
        />
        <div className="flex flex-wrap gap-4 text-muted-foreground text-xs">
          <span>
            {wordCount} {strings.words}
          </span>
          <span>
            {lineCount} {strings.lines}
          </span>
          <span>
            {paragraphCount} {strings.paragraphs}
          </span>
        </div>
      </section>

      <section className="space-y-3">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="citation-input"
        >
          {strings.citationLabel}
        </label>
        <input
          className="w-full rounded-lg border border-border bg-card px-4 py-2.5 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="citation-input"
          onChange={(e) => setCitation(e.target.value)}
          placeholder={strings.citationPlaceholders[style]}
          type="text"
          value={citation}
        />
      </section>

      {quote.trim().length > 0 && (
        <section
          className={`rounded-lg border p-4 ${
            isBlock
              ? "border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30"
              : "border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950/30"
          }`}
        >
          <p className="font-medium text-sm">
            {isBlock ? strings.isBlock : strings.isInline}
          </p>
          <p className="mt-1 text-muted-foreground text-xs">
            {countLine(
              style,
              verse,
              {
                words: wordCount,
                lines: lineCount,
                paragraphs: paragraphCount,
              },
              strings
            )}
          </p>
        </section>
      )}

      {formattedPreview && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-foreground text-sm">
              {strings.previewHeading}
            </h2>
            <button
              className="rounded-md border border-border bg-card px-3 py-1.5 text-foreground text-xs transition-colors hover:bg-accent"
              onClick={handleCopy}
              type="button"
            >
              {copied ? strings.copied : strings.copy}
            </button>
          </div>
          <div className="rounded-lg border border-border bg-card p-6">
            <pre className="whitespace-pre-wrap font-serif text-foreground text-sm leading-relaxed">
              {formattedPreview}
            </pre>
          </div>
        </section>
      )}
    </>
  );
}
